import React, { useEffect, useMemo, useState } from 'react';
import './App.css';
import PublicView from './views/PublicView';
import TelegramView from './views/TelegramView';

function App() {
    const [mode, setMode] = useState('loading'); // loading | public | telegram
    const [user, setUser] = useState(null);
    const [events, setEvents] = useState([]);
    const [myRegistrations, setMyRegistrations] = useState([]);
    const [selectedEventId, setSelectedEventId] = useState('');
    const [error, setError] = useState('');

    const tg = window.Telegram?.WebApp || null;
    const hasTelegramMiniAppContext = !!(tg && tg.initData && String(tg.initData).trim().length > 0);

    const featuredEvent = useMemo(() => events[0] || null, [events]);

    const selectedEvent = useMemo(() => {
        if (!events.length) return null;
        if (!selectedEventId) return events[0];
        return events.find((e) => String(e._id) === String(selectedEventId)) || events[0];
    }, [events, selectedEventId]);

    const selectedRegistration = useMemo(() => {
        if (!selectedEvent) return null;
        return myRegistrations.find((r) => String(r.eventId) === String(selectedEvent._id)) || null;
    }, [myRegistrations, selectedEvent]);

    const isAdmin = !!user?.roles?.includes('admin');

    async function apiFetch(url, options = {}) {
        const res = await fetch(url, {
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
                ...(options.headers || {})
            },
            ...options
        });

        const data = await res.json().catch(() => null);

        if (!res.ok) {
            const err = new Error(data?.message || data?.error || 'Request failed');
            err.payload = data;
            throw err;
        }

        return data;
    }

    async function loadEvents() {
        try {
            const data = await apiFetch('/api/events', { method: 'GET' });
            const nextEvents = Array.isArray(data?.events) ? data.events : [];
            setEvents(nextEvents);

            if (nextEvents.length > 0 && !selectedEventId) {
                setSelectedEventId(nextEvents[0]._id);
            }
        } catch (err) {
            console.error('Failed to load events:', err);
            setEvents([]);
        }
    }

    async function loadMyRegistrations() {
        try {
            const data = await apiFetch('/api/registrations/me', { method: 'GET' });
            setMyRegistrations(Array.isArray(data?.registrations) ? data.registrations : []);
        } catch (err) {
            console.error('Failed to load registrations:', err);
            setMyRegistrations([]);
        }
    }

    useEffect(() => {
        let active = true;

        async function boot() {
            try {
                await loadEvents();

                if (hasTelegramMiniAppContext) {
                    try { tg.ready(); } catch (_) {}
                    try { tg.expand(); } catch (_) {}

                    const data = await apiFetch('/api/auth/telegram', {
                        method: 'POST',
                        body: JSON.stringify({ initData: tg.initData })
                    });

                    if (!active) return;

                    setUser(data?.user || null);
                    setMode('telegram');

                    if (data?.user) {
                        await loadMyRegistrations();
                    }
                } else {
                    if (active) setMode('public');
                }
            } catch (err) {
                console.error('Boot failed:', err);
                if (active) {
                    setError('Unable to initialize Y Fight.');
                    setMode(hasTelegramMiniAppContext ? 'telegram' : 'public');
                }
            }
        }

        boot();

        return () => {
            active = false;
        };
    }, []);

    async function handleRegistrationSuccess() {
        await loadMyRegistrations();
        await loadEvents();

        if (tg?.HapticFeedback) {
            try {
                tg.HapticFeedback.notificationOccurred('success');
            } catch (_) {}
        }
    }

    if (mode === 'loading') {
        return <div className="loader">LOADING...</div>;
    }

    return (
        <div className="App">
            <div className="top-nav">
                <div className="brand-main">Y FIGHT</div>
            </div>

            {mode === 'public' && (
                <PublicView
                    featuredEvent={featuredEvent}
                    events={events}
                />
            )}

            {mode === 'telegram' && (
                <TelegramView
                    user={user}
                    isAdmin={isAdmin}
                    events={events}
                    selectedEvent={selectedEvent}
                    selectedEventId={selectedEventId}
                    setSelectedEventId={setSelectedEventId}
                    selectedRegistration={selectedRegistration}
                    onRegistrationSuccess={handleRegistrationSuccess}
                    error={error}
                />
            )}
        </div>
    );
}

export default App;